import { useCallback, useEffect, useRef, useState } from 'react'

import { type InlineEditTextFieldStatus } from '@procurify/ui'
import { truncate } from 'lodash'

import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { type TermsMethodsKindEnum } from '@webapp/modules/purchasing/entities/enums/TermsMethodsKindEnum'
import messages from './messages'
import {
  type TermOrMethod,
  type TermOrMethodEditableField,
  useMutateEditTermOrMethod,
} from '../../models'

const RESOLVED_DISPLAY_DURATION_MS = 2000

// Angular `_truncate(name, { length: 12 })` toast parity
const TOAST_NAME_TRUNCATE_LENGTH = 12

export interface UseEditableTermOrMethodTextFieldPresenterParams {
  kind: TermsMethodsKindEnum
  item: TermOrMethod
  field: TermOrMethodEditableField
  onSuccess?: (id: number) => void
}

export const useEditableTermOrMethodTextFieldPresenter = ({
  kind,
  item,
  field,
  onSuccess,
}: UseEditableTermOrMethodTextFieldPresenterParams) => {
  const intl = useProcurifyIntl()
  const persistedValue = item[field]
  const [inputValue, setInputValue] = useState(persistedValue)
  const [status, setStatus] = useState<InlineEditTextFieldStatus>('idle')
  const isMounted = useRef(true)
  const isCommittingRef = useRef(false)
  const resolvedStatusTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  )
  const { mutateAsync } = useMutateEditTermOrMethod(kind)

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
      if (resolvedStatusTimeoutRef.current) {
        clearTimeout(resolvedStatusTimeoutRef.current)
        resolvedStatusTimeoutRef.current = null
      }
    }
  }, [])

  // Keep inputValue in sync if the item prop changes externally (refetch)
  useEffect(() => {
    setInputValue(persistedValue)
  }, [persistedValue])

  const handleCommit = useCallback(async () => {
    if (isCommittingRef.current) return
    isCommittingRef.current = true

    try {
      const trimmedInputValue = inputValue.trim()

      // No-op if value unchanged
      if (trimmedInputValue === persistedValue) {
        return
      }

      // Both fields are required server-side — revert instead of PUTting an
      // empty value
      if (trimmedInputValue === '') {
        setInputValue(persistedValue)
        return
      }

      setStatus('pending')

      try {
        await mutateAsync({ item, field, value: trimmedInputValue })

        if (!isMounted.current) return

        setStatus('resolved')

        // Angular toasts the post-edit name (server-echoed row)
        const updatedName = field === 'name' ? trimmedInputValue : item.name

        toast.success({
          title: intl.formatMessage(messages.successTitle),
          children: intl.formatMessage(messages.successMessage, {
            name: truncate(updatedName, {
              length: TOAST_NAME_TRUNCATE_LENGTH,
            }),
          }),
        })

        // The Feature Set sets the UPDATED indicator (BHV-030)
        onSuccess?.(item.id)

        resolvedStatusTimeoutRef.current = setTimeout(() => {
          if (!isMounted.current) return
          setStatus('idle')
          resolvedStatusTimeoutRef.current = null
        }, RESOLVED_DISPLAY_DURATION_MS)
      } catch (err) {
        if (!isMounted.current) return

        setStatus('rejected')
        setInputValue(persistedValue)

        const errorMessage = getMessageFromError(err)

        toast.error({
          title: intl.formatMessage(messages.errorTitle),
          children: errorMessage || intl.formatMessage(messages.errorFallback),
        })
      }
    } finally {
      isCommittingRef.current = false
    }
  }, [inputValue, persistedValue, item, field, mutateAsync, intl, onSuccess])

  const handleEscape = useCallback(() => {
    setInputValue(persistedValue)
    setStatus('idle')
  }, [persistedValue])

  const handleChange = useCallback((value: string) => {
    setInputValue(value)
  }, [])

  return {
    data: {
      inputValue,
      status,
    },
    handlers: {
      handleChange,
      handleCommit,
      handleEscape,
    },
  }
}
